import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import type { SxProps } from "@mui/material/styles";
import { FEATURE_DISPLAY_NAMES } from "@polifonia/interlude/billingConstants";
import type { FeatureLimit } from "@polifonia/interlude/models";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { NumberFormat } from "@/components/NumberFormat";
import { Typography } from "@/components/Typography";

const styles = {
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    padding: "16px 0",
    borderBottom: `1px solid ${theme.colors.neutral020}`,
  },
  topRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  left: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  icon: {
    color: theme.colors.blue060,
    fontSize: "20px",
  },
  right: {
    display: "flex",
    alignItems: "baseline",
    gap: "4px",
  },
  progressBar: {
    height: "6px",
    borderRadius: "3px",
    backgroundColor: theme.colors.neutral020,
    "& .MuiLinearProgress-bar": {
      borderRadius: "3px",
      backgroundColor: theme.colors.blue060,
    },
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

interface FeatureUsageRowProps {
  feature: FeatureLimit;
  isActive: boolean;
}

const getProgressValue = (usage: number, limit: number): number => {
  if (limit === 0) return 0;
  return Math.min((usage / limit) * 100, 100);
};

export const FeatureUsageRow: React.FC<FeatureUsageRowProps> =
  function FeatureUsageRow({ feature, isActive }) {
    const { t } = useTranslation();
    const progressValue = useMemo(
      () => getProgressValue(feature.usage, feature.limit),
      [feature.usage, feature.limit],
    );

    return (
      <Box sx={styles.root}>
        <Box sx={styles.topRow}>
          <Box sx={styles.left}>
            <Typography variant="body">
              {FEATURE_DISPLAY_NAMES[feature.feature]}
            </Typography>
          </Box>
          <Box sx={styles.right}>
            {isActive ? (
              <Typography variant="body">
                <NumberFormat value={feature.usage} />
                {feature.isActive && feature.limit > 0 && (
                  <>
                    {" / "}
                    <NumberFormat value={feature.limit} />
                  </>
                )}
              </Typography>
            ) : (
              <Typography variant="body" color={theme.colors.neutral050}>
                {t("billing.usage.notSupportedYet")}
              </Typography>
            )}
          </Box>
        </Box>
        <LinearProgress
          variant="determinate"
          value={isActive ? progressValue : 0}
          sx={styles.progressBar}
        />
      </Box>
    );
  };
